(ns arthur.domain.params
  "Definitions for generated settings: defaults, scope and value constraints, once.

  WHAT IS NOT HERE. A knob's INVALIDATION — which stored bytes stop being valid
  when it moves — is `arthur.flow.address/block-knobs`, and it is there rather
  than here for two reasons that are not about layering.

  It is per BLOCK and this registry is per AREA, and the difference is not
  granularity, it is disagreement. `:aperture-cut` is a mouth setting that reaches
  the TEETH block's bytes and does not reach the mouth's, because it gates the
  contour smoothing and the mouth's own geometry is smoothed either way.
  `:blink-cut` is an eye setting that reaches no block at all, because a blink is
  `[:vis]` keys in tier 1. An `:affects #{:eye}` on that entry read as documentation
  and was wrong in both directions.

  And `block-knobs` is TESTED — `address-test` re-freezes the take once per knob
  and asserts the biconditional — while a field here could only be believed. An
  earlier version of this namespace carried `:affects` and an `affected-areas`
  reading it, and the only thing that ever called it was a test asserting it
  returned what it was written as. Two tables where one is checked and one is not
  is worse than one table, because the unchecked one is the one a parameter UI
  would reach for first. `address/invalidates` is the derived inverse, and it
  cannot drift from the thing that is asserted.")

(def definitions
  {:anchor-avg    {:area :subject :default 2 :type :integer :min 0}
   :contour-avg   {:area :subject :default 1 :type :integer :min 0}
   :verts         {:area :mouth :default 8 :type :integer :min 4 :even? true}
   :aperture-cut  {:area :mouth :default 0.12 :type :number :min 0 :max 1}
   :eye-verts     {:area :eye :default 8 :type :integer :min 4 :even? true}
   :blink-cut     {:area :eye :default 0.13 :type :number :min 0}
   :gaze-gain     {:area :eye :default 1 :type :number :min 0}
   :gaze-step     {:area :eye :default 0.08 :type :number :min 0}
   :iris-size     {:area :eye :default 0.42 :type :number :min 0}
   :lash-weight   {:area :eye :default 0.06 :type :number :min 0}
   :pupil-size    {:area :eye :default 0.15 :type :number :min 0}
   :brow-verts    {:area :brow :default 6 :type :integer :min 4 :even? true}
   :brow-gain     {:area :brow :default 1 :type :number :min 0}
   :brow-step     {:area :brow :default 0.08 :type :number :min 0}
   :brow-weight   {:area :brow :default 0.05 :type :number :min 0}
   :cavity-erode  {:area :teeth :default 0.18 :type :number :min 0}
   :tongue-reject {:area :teeth :default 0.18 :type :number :min 0}
   :blob-grow     {:area :teeth :default 0 :type :integer}
   :top-bias      {:area :teeth :default 0.6 :type :number}
   :teeth-verts   {:area :teeth :default 10 :type :integer :min 4}
   :min-area      {:area :teeth :default 12 :type :integer :min 0}
   :teeth-on      {:area :teeth :default 0.12 :type :number :min 0}
   :teeth-smooth  {:area :teeth :default 1 :type :integer :min 0}})

(def defaults
  (into {} (map (fn [[id spec]] [id (:default spec)])) definitions))

(def areas #{:subject :mouth :eye :brow :teeth})

(defn for-area [wanted-area]
  (into {} (keep (fn [[id {:keys [area default]}]]
                   (when (= wanted-area area) [id default])))
        definitions))

(defn valid-value? [id value]
  (when-let [{:keys [type min max] must-even? :even?} (get definitions id)]
    (and (case type
           :integer (integer? value)
           :number (number? value)
           false)
         (or (nil? min) (<= min value))
         (or (nil? max) (<= value max))
         (or (not must-even?) (and (integer? value) (even? value))))))

(defn valid-settings? [area settings]
  (and (map? settings)
       (every? (fn [[id value]]
                 (and (= area (get-in definitions [id :area]))
                      (valid-value? id value)))
               settings)))
